import type { PremiumPricing } from "@/feature-subscriber/model-pro-pricing.mjs";
import type { PaymentPlanInterval } from "@/feature-wallet/models/payment-plans.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import AdFreeIcon from "@/inline-assets/adfree-icon.svg";
import CautionBgFull from "@/inline-assets/caution.svg";
import DontNeed from "@/inline-assets/circle-close.svg";
import LackOfFeatures from "@/inline-assets/lack-of-features.svg";
import Other from "@/inline-assets/nav-more.svg";
import OverlayIcon from "@/inline-assets/overlay-icon.svg";
import ProbuildsIcon from "@/inline-assets/probuild-icon.svg";
import TooExpensive from "@/inline-assets/too-expensive.svg";

export const PRO_ENABLED_GAMES = [
  GAME_SYMBOL_LOL,
  GAME_SYMBOL_VAL,
  GAME_SYMBOL_TFT,
];

export const COUPON_DURATION = {
  FOREVER: "forever",
  ONCE: "once",
  MONTH: "repeating",
};

export const FEATURES_LITE = {
  [GAME_SYMBOL_LOL]: {
    features: [
      [
        "common:subscriber.compareLolFeatures.lite.autoImportRunesItemBuilds",
        "Auto-import runes & item builds",
      ],
      [
        "common:subscriber.compareLolFeatures.lite.importProbuildsViewLiveProGames",
        "Import pro builds & view live pro games",
      ],
      [
        "common:subscriber.compareLolFeatures.lite.lpTrackingForAllChampionsRoles",
        "LP tracking for all Champions & roles",
      ],
      [
        "common:subscriber.compareLolFeatures.lite.basicIngameOverlays",
        "Basic in-game overlays",
      ],
    ],
  },
  [GAME_SYMBOL_VAL]: {
    features: [
      [
        "common:subscriber.compareValorantFeatures.lite.personalPerformanceStats",
        "Personal performance stats",
      ],
      [
        "common:subscriber.compareValorantFeatures.lite.postMatchReports",
        "Post-match reports",
      ],
      [
        "common:subscriber.compareValorantFeatures.lite.videoGuides",
        "Video guides",
      ],
      [
        "common:subscriber.compareValorantFeatures.lite.interactiveMaps",
        "Interactive maps",
      ],
      [
        "common:subscriber.compareValorantFeatures.lite.agentMapWeaponStats",
        "Agent, map & weapon stats",
      ],
      [
        "common:subscriber.compareValorantFeatures.lite.leaderboards",
        "Leaderboards",
      ],
    ],
  },
  [GAME_SYMBOL_TFT]: {
    features: [
      [
        "common:subscriber.compareTftFeatures.lite.personalPerformanceStats",
        "Personal performance stats",
      ],
      [
        "common:subscriber.compareTftFeatures.lite.championTraitItemStats",
        "Champion, trait & item stats",
      ],
      [
        "common:subscriber.compareTftFeatures.lite.compRankingsProBuilds",
        "Comp rankings & pro builds",
      ],
      [
        "common:subscriber.compareTftFeatures.lite.ingameOverlays",
        "In-game overlays",
      ],
      [
        "common:subscriber.compareTftFeatures.lite.postMatchPerformanceAnalysis",
        "Post-match performance analysis",
      ],
      [
        "common:subscriber.compareTftFeatures.lite.postMatchRoundBreakdownsLimited",
        "Post-match round breakdowns for 24 hours after match end",
      ],
    ],
  },
};

export const FEATURES_PRO = {
  [GAME_SYMBOL_LOL]: {
    features: [
      [
        "common:subscriber.compareLolFeatures.subscriber.allBlitzLiteFeatures",
        "All Blitz Lite features",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.adFreeExperience",
        "Ad-free experience",
      ],
      {
        name: [
          "common:subscriber.compareLolFeatures.subscriber.proBuilds",
          "Pro Builds+",
        ],
        info: [
          "common:subscriber.paymentBillingPage.proBuildsDataTip",
          "Import competitive Pro Builds from official tournament matches and Masters+ games. Plus, you'll get dynamic high-tier build suggestions optimized for team synergy.",
        ],
      },
      [
        "common:subscriber.compareLolFeatures.subscriber.customizableCsOverlay",
        "Customizable CS overlay",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.supportOverlay",
        "Support overlay",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.supportPostMatchReports",
        "Support post-match reports",
      ],
    ],
  },
  [GAME_SYMBOL_VAL]: {
    features: [
      [
        "common:subscriber.compareLolFeatures.subscriber.allBlitzLiteFeatures",
        "All Blitz Lite features",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.adFreeExperience",
        "Ad-free experience",
      ],
    ],
    upcoming: [
      [
        "common:subscriber.compareValorantFeatures.subscriber.sprayControlOverlay",
        "Spray control overlay",
      ],
      [
        "common:subscriber.compareValorantFeatures.subscriber.statisticsOverlay",
        "Statistics overlay",
      ],
      [
        "common:subscriber.compareValorantFeatures.subscriber.advancedPerformanceAnalysis",
        "Advanced performance analysis",
      ],
    ],
  },
  [GAME_SYMBOL_TFT]: {
    features: [
      [
        "common:subscriber.compareLolFeatures.subscriber.allBlitzLiteFeatures",
        "All Blitz Lite features",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.adFreeExperience",
        "Ad-free experience",
      ],
      [
        "common:subscriber.compareLolFeatures.subscriber.postMatchRoundBreakdownsUnlimited",
        "Post-match round breakdowns for unlimited time",
      ],
    ],
  },
};

export const SUBSCRIBER_PAYMENT_PLAN = {
  month: {
    planId: "BLITZ_PRO_MONTHLY",
    getRenewalDateFrom(fromDate: Date) {
      const renewalDate = new Date(fromDate);
      renewalDate.setMonth(renewalDate.getMonth() + 1);
      return renewalDate;
    },
  },
} satisfies Record<
  PaymentPlanInterval,
  { planId: string; getRenewalDateFrom(fromDate: Date): Date }
>;

interface CancelFeatureEntry {
  Icon: SVGComponent;
  text: Translation;
}

export const CANCEL_FEATURES: ReadonlyArray<CancelFeatureEntry> = [
  {
    Icon: AdFreeIcon,
    text: [
      "common:wallet.paymentBillingPage.adFreeExperience",
      "Ad-Free Experience",
    ],
  },
  {
    Icon: ProbuildsIcon,
    text: ["common:wallet.paymentBillingPage.probuilds", "Pro Builds+"],
  },
  {
    Icon: OverlayIcon,
    text: [
      "common:wallet.paymentBillingPage.supportOverlay",
      "Support Overlay",
    ],
  },
  {
    Icon: OverlayIcon,
    text: [
      "common:wallet.paymentBillingPage.customizableCsOverlay",
      "Customizable CS Overlay",
    ],
  },
];

interface CancelReasonEntry {
  Icon: SVGComponent;
  text: Translation;
}

export const CANCEL_REASONS: ReadonlyArray<CancelReasonEntry> = [
  {
    Icon: TooExpensive,
    text: [
      "common:wallet.paymentBillingPage.reason.tooExpensive",
      "Too Expensive",
    ],
  },
  {
    Icon: CautionBgFull,
    text: [
      "common:wallet.paymentBillingPage.reason.technicalIssues",
      "Technical Issues",
    ],
  },
  {
    Icon: LackOfFeatures,
    text: [
      "common:wallet.paymentBillingPage.reason.lackOfFeatures",
      "Lack of Features",
    ],
  },
  {
    Icon: DontNeed,
    text: [
      "common:wallet.paymentBillingPage.reason.iDontNeedIt",
      "I Don't Need It",
    ],
  },
  {
    Icon: Other,
    text: ["common:wallet.paymentBillingPage.reason.other", "Other"],
  },
];

declare module "@/__main__/app-state.mjs" {
  interface FeatureState {
    subscriber: {
      pricing: PremiumPricing;
    };
  }
}
